import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import Dashboard from './components/Dashboard/Dashboard';
import PlayerList from './components/Players/PlayerList';
import TournamentList from './components/Tournaments/TournamentList';
import PlayerScoring from './components/Scoring/PlayerScoring';
import MobileScoreEntry from './components/Tournaments/MobileScoreEntry';
import LiveScoreboard from './components/Tournaments/LiveScoreboard';
import MatchPlayTournament from './components/Tournaments/MatchPlayTournament';
import TeeTimeManagement from './components/Tournaments/TeeTimeManagement';
import StablefordScoreboard from './components/Tournaments/StablefordScoreboard';
import MultiRoundTournament from './components/Tournaments/MultiRoundTournament';
import QRCodeManager from './components/Tournaments/QRCodeManager';
import TournamentDirector from './components/Tournaments/TournamentDirector';
import PayoutManagement from './components/Tournaments/PayoutManagement';
import ScorerInterface from './components/Tournaments/ScorerInterface';
import QRCodeDebug from './components/Tournaments/QRCodeDebug';
import MobileScoreTest from './components/Tournaments/MobileScoreTest';
import SkinsManagement from './components/Tournaments/SkinsManagement';
import SkinsDebug from './components/Tournaments/SkinsDebug';
import TransactionLog from './components/Admin/TransactionLog';
import Settings from './components/Admin/Settings';
import BackupsRestore from './components/Admin/BackupsRestore';
import UserProfile from './components/Admin/UserProfile';
import UserManagement from './components/Admin/UserManagement';
import GolfCourseList from './components/GolfCourses/GolfCourseList';
import './App.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2e7d32',
    },
    secondary: {
      main: '#f57c00',
    },
  },
});

function ProtectedRoute({ children }) {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" />;
}

function PublicRoute({ children }) {
  const { currentUser } = useAuth();
  return currentUser ? <Navigate to="/dashboard" /> : children;
}

function AppContent() {
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          <Route 
            path="/signup" 
            element={
              <PublicRoute>
                <Signup />
              </PublicRoute>
            } 
          />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/players" 
            element={
              <ProtectedRoute>
                <Layout>
                  <PlayerList />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments" 
            element={
              <ProtectedRoute>
                <Layout>
                  <TournamentList />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments/:tournamentId/scoreboard" 
            element={
              <ProtectedRoute>
                <Layout>
                  <LiveScoreboard />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments/:tournamentId/stableford" 
            element={
              <ProtectedRoute>
                <Layout>
                  <StablefordScoreboard />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments/:tournamentId/multiround" 
            element={
              <ProtectedRoute>
                <Layout>
                  <MultiRoundTournament />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments/:tournamentId/matchplay" 
            element={
              <ProtectedRoute>
                <Layout>
                  <MatchPlayTournament />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments/:tournamentId/director" 
            element={
              <ProtectedRoute>
                <Layout>
                  <TournamentDirector />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments/:tournamentId/qr-codes" 
            element={
              <ProtectedRoute>
                <Layout>
                  <QRCodeManager />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments/:tournamentId/teetimes" 
            element={
              <ProtectedRoute>
                <Layout>
                  <TeeTimeManagement />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments/:tournamentId/payouts" 
            element={
              <ProtectedRoute>
                <Layout>
                  <PayoutManagement />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments/:tournamentId/scorer" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ScorerInterface />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/debug/qr-codes" 
            element={
              <ProtectedRoute>
                <Layout>
                  <QRCodeDebug />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/test/mobile-scoring" 
            element={
              <ProtectedRoute>
                <Layout>
                  <MobileScoreTest />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments/:tournamentId/skins" 
            element={
              <ProtectedRoute>
                <Layout>
                  <SkinsManagement />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tournaments/:tournamentId/skins-debug" 
            element={
              <ProtectedRoute>
                <Layout>
                  <SkinsDebug />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/scoring/:tournamentId/:playerId" 
            element={<PlayerScoring />} 
          />
          <Route 
            path="/mobile-scoring/:qrCode" 
            element={<MobileScoreEntry />} 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/golf-courses" 
            element={
              <ProtectedRoute>
                <Layout>
                  <GolfCourseList />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings/backups-restore" 
            element={
              <ProtectedRoute>
                <Layout>
                  <BackupsRestore />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings/transaction-log" 
            element={
              <ProtectedRoute>
                <Layout>
                  <TransactionLog />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings/profile" 
            element={
              <ProtectedRoute>
                <Layout>
                  <UserProfile />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings/users" 
            element={
              <ProtectedRoute>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </ThemeProvider>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
